import { Box, Stack } from '@mui/material';

import { Button } from '@/components/form/baseInputs/Button';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import { downloadJSON } from '@/modules/files/utils/fileUtils';

import { getClientsOptions, useClientData } from './ClientImportExport.utils';
import { useExportAdvisorClientMutation } from './graphql/ClientImportExport.generated';

interface AdvisorClientCloneCardFormShape {
  householdId: string;
}

export function ClientExport() {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const { loading: clientDataLoading, clientsData } = useClientData();

  const [exportHousehold] = useExportAdvisorClientMutation({
    onError: (err) => {
      showFeedback('Failed to export client');
      reportError('could not export client', err);
    },
  });

  const formMethods = useForm<AdvisorClientCloneCardFormShape>();
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = formMethods;

  async function onClientExport(formValues: AdvisorClientCloneCardFormShape) {
    return exportHousehold({
      variables: { id: formValues.householdId },
      onCompleted: (data) => {
        if (data?.exportHousehold) {
          downloadJSON(
            data.exportHousehold,
            `export_${formValues.householdId}.json`
          );
        }
      },
    });
  }

  return (
    <FormProvider {...formMethods}>
      <Stack
        spacing={2}
        direction="row"
        justifyContent="space-between"
        alignItems="end"
        noValidate
        component="form"
        onSubmit={handleSubmit(onClientExport)}
      >
        <Box sx={{ flexGrow: 1 }}>
          <FormAwareTypeaheadSelectInput<AdvisorClientCloneCardFormShape>
            label="Client to export"
            fieldName={'householdId'}
            required
            options={getClientsOptions(clientsData)}
            disabled={isSubmitting || clientDataLoading}
            control={control}
          />
        </Box>
        <Box>
          <Button
            loading={isSubmitting}
            disabled={clientDataLoading}
            variant="primary"
            size="sm"
            type="submit"
          >
            Export client
          </Button>
        </Box>
      </Stack>
    </FormProvider>
  );
}
